import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/hooks';
import { LoadingFallback } from '@components/shared/LoadingFallback';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Executive surface lives entirely under `/exec`. Users with the
 * `executive` role MUST be on `/exec/*`, and conversely no other
 * role is allowed to peek at `/exec/*` — that's enforced here so a
 * URL jump cannot bypass the dedicated layout.
 */
function isExecPath(pathname: string): boolean {
  return pathname === '/exec' || pathname.startsWith('/exec/');
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const sessionBootstrapComplete = useAppSelector((state) => state.auth.sessionBootstrapComplete);
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();

  if (!sessionBootstrapComplete) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isExecutive = Boolean(user?.roles?.includes('executive'));
  const onExecPath = isExecPath(location.pathname);

  // Executives outside /exec/* → push them into their dedicated surface.
  if (isExecutive && !onExecPath) {
    return <Navigate to="/exec/dashboard" replace />;
  }
  // Non-executives trying to hit /exec/* → bounce back to the
  // admin app. The PermissionGate would 403 them anyway, but
  // sending them home is friendlier than the fallback Result.
  if (!isExecutive && onExecPath) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
